import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import { Button, Grid, useMediaQuery } from "@mui/material";
import PropTypes from "prop-types";
import moment from "moment";

import Backdrop from "Components/Common/Backdrop";
import BreadCrumb from "Components/Common/BreadCrumb";
import BaseTable from "Components/Common/BaseTable";
import DatePicker from "Components/Common/DatePicker";
import SelectField from "Components/Common/SelectField";
import { billingSubscriptionTypes } from "common/enums";
import InputFieldText from "Components/Common/InputFieldText";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import { isAuthorizedUser } from "utils";

import { useLazyGetAbstractNoticeReportQuery } from "apis/Billing";
import { useLazyDownloadExcelQuery } from "slices/api";

const RECORDS_PER_PAGE = 10;

const initialQuery = {
  meterNumber: "",
  consumerNumber: "",
  dateTime: moment(),
};

const columns = [
  {
    title: "Consumer Name",
    field: "consumerName",
  },
  {
    title: "Consumer Number",
    field: "consumerNumber",
  },
  {
    title: "Email",
    field: "email",
  },
  {
    title: "Meter Number",
    field: "meterNumber",
  },
  {
    title: "Status",
    field: "status",
  },
  {
    title: "Notice Date Time",
    renderCell: ({ dispatchedDateTime }) =>
      convertUTCDateToLocalDate(dispatchedDateTime).format("DD-MM-YYYY"),
  },
];

const PowerOutstandingNotice = ({ t }) => {
  const [query, setQuery] = useState(initialQuery);
  const [subscriptionType, setSubscriptionType] = useState("");
  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);

  const resetPageRef = useRef();

  const { roles } = useSelector((state) => state.Login);

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const [
    getOutstandingNoticeData,
    { data: { data: abstractNoticeData } = {}, isLoading, isFetching },
  ] = useLazyGetAbstractNoticeReportQuery();

  const [downloadExcel, { isLoading: isExcelFileDownloading }] =
    useLazyDownloadExcelQuery();

  const sm = useMediaQuery("(max-width: 599px)");
  const isLargeScreen = useMediaQuery("(max-width: 1590px)");

  const updatedBillingSubscriptionOptions = useMemo(
    () =>
      billingSubscriptionTypes.filter(({ allowedRoles }) =>
        isAuthorizedUser(roles, allowedRoles)
      ),
    [billingSubscriptionTypes]
  );

  const downloadAbstractNoticeReport = async () => {
    try {
      await downloadExcel({
        endpoint: "/report/outstanding-bill-notice-report-excel",
        sheetName: "Outstanding Notice Report",
        query: {
          dateTime: encodeURIComponent(query.dateTime.format("YYYY-MM-DD")),
        },
      });
    } catch (error) {}
  };

  useEffect(() => {
    if (subscriptionType) {
      getOutstandingNotice(initialQuery);
    }
  }, [subscriptionType]);

  useEffect(() => {
    if (updatedBillingSubscriptionOptions.length) {
      setSubscriptionType(updatedBillingSubscriptionOptions[0].value);
    }
  }, [updatedBillingSubscriptionOptions]);

  const getOutstandingNotice = async (query) => {
    try {
      await getOutstandingNoticeData({
        ...query,
        dateTime: encodeURIComponent(query.dateTime.format("YYYY-MM-DD")),
        subscriptionType,
      });
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <Backdrop open={isLoading || isFetching || isExcelFileDownloading} />
      <BreadCrumb title={t("Outstanding Notice")} pageTitle={t("Billing")} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          getOutstandingNotice(query);
          handleResetPage();
        }}
      >
        <Grid container columnSpacing={2} rowSpacing={1}>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SelectField
              label={t("Subscription Type") + "*"}
              onChange={(_, { value }) => {
                setSubscriptionType(value);
              }}
              value={
                updatedBillingSubscriptionOptions.find(
                  ({ value }) => value === subscriptionType
                ) ?? ""
              }
              options={updatedBillingSubscriptionOptions}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <DatePicker
              slotProps={{
                textField: {
                  size: "small",
                },
              }}
              label={t("Select Month and Year")}
              className="w-100"
              views={["month", "year"]}
              value={query.dateTime}
              disableFuture
              onChange={(date) => {
                setQuery((prev) => ({
                  ...prev,
                  dateTime: date,
                }));
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputFieldText
              label={t("Consumer Number")}
              name="consumerNumber"
              type="number"
              onChange={(e) => {
                setQuery((prev) => ({
                  ...prev,
                  consumerNumber: e.target.value,
                }));
              }}
              value={query.consumerNumber}
              placeholder={t("Enter consumer number")}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputFieldText
              label={t("Meter Number")}
              onChange={(e) => {
                setQuery((prev) => ({ ...prev, meterNumber: e.target.value }));
              }}
              value={query.meterNumber}
              placeholder={t("Enter meter number")}
            />
          </Grid>

          <Grid item lg={isLargeScreen ? 5 : 3}>
            <Button variant="outlined" className="mb-2 me-2" type="submit">
              {t("Search")}
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                getOutstandingNotice(initialQuery);
                setQuery(initialQuery);
                handleResetPage();
              }}
              className="mb-2 me-2"
            >
              {t("Clear")}
            </Button>
            <Button
              variant="contained"
              className="mb-2 me-2"
              onClick={downloadAbstractNoticeReport}
            >
              {t("Download")}
            </Button>
          </Grid>
        </Grid>
      </form>
      <BaseTable
        columns={columns}
        data={abstractNoticeData ?? []}
        pageSize={RECORDS_PER_PAGE}
        registerResetPage={(cb) => {
          resetPageRef.current = cb;
          setIsResetPageRegistered((prev) => !prev);
        }}
        displaySrNo={!!abstractNoticeData?.length}
        maxHeight={sm ? "calc(100vh - 300px)" : "calc(100vh - 250px)"}
      />
    </>
  );
};

PowerOutstandingNotice.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(PowerOutstandingNotice);
